import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Center,
  Heading,
  useToast,
  FormControl,
  FormErrorMessage,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { resetPassword, signOut, validateToken } from "../../services/authService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import PasswordInput from "../../components/password-input/PasswordInput";

function ResetPassword() {
  const { t } = useTranslation("common");
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearAuthSession } = useAuth();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [canReset, setCanReset] = useState(null);
  const resetPasswordToken = searchParams.get("reset_password_token");

  useEffect(() => {
    let isMounted = true;

    const checkResetContext = async () => {
      if (resetPasswordToken) {
        if (isMounted) {
          setCanReset(true);
        }
        return;
      }

      try {
        const response = await validateToken();
        if (
          isMounted &&
          response.data.success &&
          response.data.data?.allowPasswordChange
        ) {
          setCanReset(true);
          return;
        }
      } catch (_error) {
        // fall through to redirect
      }

      if (isMounted) {
        setCanReset(false);
        navigate("/forgot-password", { replace: true });
      }
    };

    checkResetContext();

    return () => {
      isMounted = false;
    };
  }, [navigate, resetPasswordToken]);

  const mutation = useMutation({
    mutationFn: (values) =>
      resetPassword({
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
        resetPasswordToken: resetPasswordToken || undefined,
      }),
    onSuccess: async () => {
      await queryClient.cancelQueries(["validateToken"]);
      clearAuthSession();
      queryClient.removeQueries(["validateToken"]);

      try {
        await signOut();
      } catch (_error) {
        // proceed to login even if sign-out fails
      }

      toast({
        title: t("resetPasswordSuccessMessage"),
        status: "success",
      });

      navigate("/login", { replace: true });
    },
    onError: (error) => {
      const apiError = error.response?.data?.errors || error.response?.data?.error;
      const message = Array.isArray(apiError)
        ? apiError[0]
        : apiError || t("resetPasswordErrorMessage");

      toast({
        title: message,
        status: "error",
      });
    },
  });

  if (canReset === null) {
    return (
      <Center>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!canReset) {
    return null;
  }

  return (
    <Center>
      <Stack spacing={3} width={"400px"}>
        <Heading size={"lg"} mb={4}>
          {t("resetPasswordHeading")}
        </Heading>
        <Formik
          initialValues={{
            password: "",
            passwordConfirmation: "",
          }}
          validationSchema={Yup.object({
            password: Yup.string()
              .required(t("requiredFieldMessage"))
              .min(6, t("passwordTooShortMessage")),
            passwordConfirmation: Yup.string()
              .required(t("requiredFieldMessage"))
              .oneOf([Yup.ref("password"), null], t("passwordsDoNotMatchMessage")),
          })}
          onSubmit={(values) => {
            mutation.mutate(values);
          }}
        >
          {(props) => (
            <Form>
              <Stack spacing={3}>
                <Field name="password">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.password && form.touched.password}
                    >
                      <PasswordInput
                        {...field}
                        variant="filled"
                        isVisible={showPassword}
                        onToggle={() => setShowPassword((prev) => !prev)}
                        placeholder={t("passwordInputPlaceholder")}
                      />
                      <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="passwordConfirmation">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.passwordConfirmation &&
                        form.touched.passwordConfirmation
                      }
                    >
                      <PasswordInput
                        {...field}
                        variant="filled"
                        isVisible={showPasswordConfirmation}
                        onToggle={() => setShowPasswordConfirmation((prev) => !prev)}
                        placeholder={t("passwordConfirmationInputPlaceholder")}
                      />
                      <FormErrorMessage>
                        {form.errors.passwordConfirmation}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Button
                  width="full"
                  isLoading={mutation.isLoading}
                  type="submit"
                  colorScheme="whatsapp"
                  mt={4}
                >
                  {t("resetPasswordButtonText")}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </Center>
  );
}

export default ResetPassword;
